/**
 * Bill Processing WebSocket Service
 * Real-time updates for bill processing workflows
 */

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://nsgruve.ai.pocsolution.com';

class BillWebSocketService {
  constructor(token) {
    this.token = token;
    this.ws = null;
    this.runId = null;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectDelay = 2000;
    this.pingInterval = null;

    // Event handlers
    this.messageHandlers = new Set();
    this.statusHandlers = new Set();
    this.errorHandlers = new Set();

    // Connection state
    this.isConnected = false;
    this.isConnecting = false;
  }

  /**
   * Get WebSocket URL
   */
  getWebSocketUrl(runId) {
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const host = window.location.host;
    return `${protocol}//${host}/api/bill-processing-ws/ws/${runId}?token=${this.token}`;
  }

  /**
   * Connect to WebSocket for a workflow run
   */
  connect(runId) {
    if (this.isConnecting) {
      console.log('🔌 WebSocket already connecting...');
      return;
    }

    if (this.ws && this.ws.readyState === WebSocket.OPEN && this.runId === runId) {
      console.log('🔌 WebSocket already connected to', runId);
      return;
    }

    if (this.ws) {
      this.disconnect();
    }

    this.runId = runId;
    this.isConnecting = true;

    const url = this.getWebSocketUrl(runId);
    console.log('🔌 Connecting to WebSocket:', url);

    try {
      this.ws = new WebSocket(url);

      this.ws.onopen = () => {
        console.log('✅ WebSocket connected to run_id:', runId);
        this.isConnected = true;
        this.isConnecting = false;
        this.reconnectAttempts = 0;
        this.emitStatus('connected');
        this.startPingInterval();
      };

      this.ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          this.handleMessage(data);
        } catch (e) {
          console.error('❌ Failed to parse WebSocket message:', e);
        }
      };

      this.ws.onclose = (event) => {
        console.log('🔌 WebSocket closed:', event.code, event.reason);
        this.isConnected = false;
        this.isConnecting = false;
        this.stopPingInterval();
        this.emitStatus('disconnected');

        if (event.code !== 1000 && event.code !== 4001) {
          this.attemptReconnect();
        }
      };

      this.ws.onerror = (error) => {
        console.error('❌ WebSocket error:', error);
        this.isConnecting = false;
        this.emitError(error);
      };

    } catch (e) {
      console.error('❌ Failed to create WebSocket:', e);
      this.isConnecting = false;
      this.emitError(e);
    }
  }

  /**
   * Disconnect from WebSocket
   */
  disconnect() {
    this.stopPingInterval();

    if (this.ws) {
      this.ws.close(1000, 'Client disconnect');
      this.ws = null;
    }

    this.isConnected = false;
    this.isConnecting = false;
    this.runId = null;
  }

  /**
   * Reconnect with exponential backoff
   */
  attemptReconnect() {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.log('❌ Max reconnect attempts reached');
      this.emitError(new Error('Max reconnect attempts reached'));
      return;
    }

    this.reconnectAttempts++;
    const delay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1);

    console.log(`🔄 Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts})`);

    setTimeout(() => {
      if (this.runId && !this.isConnected) {
        this.connect(this.runId);
      }
    }, delay);
  }

  /**
   * Handle incoming message
   */
  handleMessage(data) {
    const msgType = data.type;

    if (msgType === 'ping') {
      this.send({ type: 'pong' });
      return;
    }

    if (msgType === 'pong') {
      return;
    }

    console.log('📨 WebSocket message:', msgType, data);

    this.messageHandlers.forEach(handler => {
      try {
        handler(data);
      } catch (e) {
        console.error('Error in message handler:', e);
      }
    });
  }

  /**
   * Send message to server
   */
  send(message) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(message));
      return true;
    }
    return false;
  }

  startPingInterval() {
    this.stopPingInterval();
    this.pingInterval = setInterval(() => {
      this.send({ type: 'ping' });
    }, 30000);
  }

  stopPingInterval() {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
  }

  // Event subscription methods
  onMessage(handler) {
    this.messageHandlers.add(handler);
    return () => this.messageHandlers.delete(handler);
  }

  onStatus(handler) {
    this.statusHandlers.add(handler);
    return () => this.statusHandlers.delete(handler);
  }

  onError(handler) {
    this.errorHandlers.add(handler);
    return () => this.errorHandlers.delete(handler);
  }

  emitStatus(status) {
    this.statusHandlers.forEach(handler => handler(status));
  }

  emitError(error) {
    this.errorHandlers.forEach(handler => handler(error));
  }

  // Convenience methods for specific message types
  on(messageType, handler) {
    return this.onMessage((data) => {
      if (data.type === messageType) handler(data);
    });
  }

  onStepStarted(handler) { return this.on('step_started', handler); }
  onStepCompleted(handler) { return this.on('step_completed', handler); }
  onStepFailed(handler) { return this.on('step_failed', handler); }
  onChatMessage(handler) { return this.on('chat_message', handler); }
  onBillDataUpdate(handler) { return this.on('bill_data_update', handler); }
  onWorkflowStatus(handler) { return this.on('workflow_status', handler); }
  onWorkflowCompleted(handler) { return this.on('workflow_completed', handler); }

  // API Methods
  async startWorkflow(workflowId, userMessage = '') {
    const response = await fetch(`${API_BASE_URL}/api/bill-processing-ws/workflow/start`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${this.token}`
      },
      body: JSON.stringify({ workflow_id: workflowId, user_message: userMessage })
    });
    return response.json();
  }

  async uploadBill(file, workflowId = 22) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('workflow_id', workflowId);

    const response = await fetch(`${API_BASE_URL}/api/bill-processing-ws/upload-bill`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${this.token}`
      },
      body: formData
    });
    return response.json();
  }

  async continueWorkflow(runId, userMessage) {
    const response = await fetch(`${API_BASE_URL}/api/bill-processing-ws/workflow/continue/${runId}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${this.token}`
      },
      body: JSON.stringify({ user_message: userMessage })
    });
    return response.json();
  }

  async getWorkflowStatus(runId) {
    const response = await fetch(`${API_BASE_URL}/api/bill-processing-ws/workflow/status/${runId}`, {
      headers: { 'Authorization': `Bearer ${this.token}` }
    });
    return response.json();
  }

  async getWorkflowMessages(runId) {
    const response = await fetch(`${API_BASE_URL}/api/bill-processing-ws/workflow/messages/${runId}`, {
      headers: { 'Authorization': `Bearer ${this.token}` }
    });
    return response.json();
  }
}

export default BillWebSocketService;
